// PlatformPage — the ecosystem view (customer app · Phase 4).
//
// Visible ONLY to a platform admin, and only ever populated by platform_* RPCs
// (sql/073). Nothing here reads a table directly: RLS still scopes this login to
// its own organization, which is exactly the point — the owner wears two hats on
// one login, so the cross-operator view has to be an explicit, separate surface
// rather than a widened policy that would leak every operator into the ordinary
// CRM pages.
//
// Read-only, except the add-on templates operators inherit.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

const HJ = () => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
});
const rpc = async (fn, body = {}) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, { method: "POST", headers: HJ(), body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.text()) || fn);
  return r.json();
};

const money = (n) => (n == null ? null : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const ago = (ts) => {
  if (!ts) return "";
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
const KIND = {
  request: ["📱", "In the app"],
  event:   ["•",  "Account"],
  signup:  ["✨", "Signup"],
};

export function PlatformPage({ showToast }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("activity");
  const [stats, setStats] = useState(null);
  const [operators, setOperators] = useState([]);
  const [activity, setActivity] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [orgFilter, setOrgFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [saved, setSaved] = useState(null);
  const [tabErr, setTabErr] = useState({});
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadCore = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([rpc("platform_stats"), rpc("platform_operators")]);
      setStats(s);
      setOperators(o);
      setErr(null);
    } catch (e) {
      setErr(/platform_admin_required/.test(e.message) ? "This area is for the platform owner." : "Couldn't load the ecosystem view.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadCore(); }, [loadCore]);

  // A swallowed error here is worse than a visible one: an empty list on a dashboard
  // reads as "nothing is happening across the platform" when the truth may be that the
  // token expired. Every tab load records why it is empty, and the seq guard stops a
  // slow earlier response from overwriting a newer one while the search box is typed in.
  const seq = useRef(0);
  const load = useCallback((key, fn, args, setter) => {
    const mine = ++seq.current;
    setTabErr((e) => ({ ...e, [key]: null }));
    rpc(fn, args).then(
      (d) => { if (mine === seq.current) setter(d); },
      () => { if (mine === seq.current) setTabErr((e) => ({ ...e, [key]: true })); },
    );
  }, []);

  useEffect(() => {
    if (tab !== "activity") return;
    load("activity", "platform_activity", { p_limit: 120, p_org: orgFilter || null, p_kind: kindFilter || null }, setActivity);
  }, [tab, orgFilter, kindFilter, load]);

  useEffect(() => {
    if (tab !== "clients") return;
    const t = setTimeout(() => {
      load("clients", "platform_clients", { p_search: search || null, p_org: orgFilter || null, p_limit: 200 }, setClients);
    }, 220);
    return () => clearTimeout(t);
  }, [tab, search, orgFilter, load]);

  useEffect(() => {
    if (tab !== "templates") return;
    load("templates", "platform_templates", {}, setTemplates);
  }, [tab, load]);

  const realOperators = useMemo(() => operators.filter((o) => !o.is_platform_shell), [operators]);

  // Opening a drill-in is its own little race: click two rows quickly and the slower
  // response can land last. Same guard as the list loads.
  const detailSeq = useRef(0);
  const openDetail = useCallback((client_id) => {
    if (!client_id) return;
    const mine = ++detailSeq.current;
    rpc("platform_client_detail", { p_client: client_id }).then(
      (d) => { if (mine === detailSeq.current) setDetail(d); },
      () => { if (mine === detailSeq.current) showToast && showToast("Couldn't open that client.", "error"); },
    );
  }, [showToast]);

  // A drill-in panel that outlives the list it came from is worse than no panel: it sits
  // beside a list the client isn't in and reads as belonging to it.
  useEffect(() => { setDetail(null); }, [orgFilter, kindFilter, search]);

  // The CRM's toast sits at the top of the document flow, so on a long scrolling
  // list it lands above the fold and the edit looks like it did nothing. Mark the
  // row itself as well, right where the operator is looking.
  async function saveTemplate(id, patch) {
    try {
      await rpc("platform_template_upsert", { p_id: id, p_patch: patch });
      setTemplates(await rpc("platform_templates"));
      setSaved(id);
      setTimeout(() => setSaved((s) => (s === id ? null : s)), 2500);
      showToast && showToast("Template saved. Operators who already added it keep their own copy.", "success");
    } catch (e) {
      setSaved(`err:${id}`);
      setTimeout(() => setSaved((s) => (s === `err:${id}` ? null : s)), 4000);
      showToast && showToast(/bad_price/.test(e.message || "") ? "That price isn't valid." : "Couldn't save that template.", "error");
      throw e;   // so a control that moved optimistically can roll itself back
    }
  }

  if (loading) return <div style={{ padding: 20, color: "var(--text2)" }}>Loading the ecosystem…</div>;
  if (err) return <div className="card" style={{ padding: 24 }}><p className="lead">{err}</p></div>;

  return (
    <div className="calm-pane">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Platform</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
            {tab === "templates"
              ? "The platform's own starter menu. Editing it never changes an operator's data."
              : "Every operator on iScoopCRM. Read-only — nothing here changes an operator's data."}
          </div>
        </div>
        <div className="seg">
          {[["activity", "Activity"], ["operators", "Operators"], ["clients", "Clients"], ["templates", "Add-on templates"]].map(([k, l]) => (
            <button key={k} className={tab === k ? "on" : ""} onClick={() => { setTab(k); setDetail(null); }}>{l}</button>
          ))}
        </div>
      </div>

      {stats ? (
        <div className="stats-row" style={{ marginBottom: 14 }}>
          <Stat label="Operators" value={stats.operators} sub={`${stats.operators_live} billing live · ${stats.operators_app_on} on the app`} accent />
          <Stat label="Active clients" value={stats.active_clients?.toLocaleString()}
                sub={`${stats.app_logins} of them have an app account`} />
          {/* mrr_discounted is the same population with each subscription's discount applied,
              so the caveat no longer has to disappear the moment a test org exists. */}
          <Stat label="Billed monthly" value={money(stats.mrr_discounted ?? stats.mrr_listed)}
                sub={[
                  Number(stats.mrr_listed) > Number(stats.mrr_discounted ?? stats.mrr_listed)
                    ? `after discounts · ${money(stats.mrr_listed)} list` : "after discounts",
                  Number(stats.mrr_simulated) > 0 ? `${money(stats.mrr_simulated)} more in test orgs` : null,
                ].filter(Boolean).join(" · ")} />
          {/* The percentage fee is only half of platform revenue — flat-plan operators pay
              $99/mo directly (sql/012). A $0 here means nobody is being charged, which is
              a different statement from "everyone is at 0%". */}
          <Stat label="Platform revenue" value={money(Number(stats.platform_fee_monthly) + Number(stats.flat_monthly || 0))}
                sub={[
                  stats.operators_charged ? `${stats.operators_charged} paying a %` : null,
                  stats.operators_flat ? `${stats.operators_flat} on $99 flat` : null,
                ].filter(Boolean).join(" · ") || "nobody is being charged yet"} />
          <Stat label="Open requests" value={stats.requests_open}
                sub={`${stats.requests_7d} customer action${stats.requests_7d === 1 ? "" : "s"} in 7 days`} />
        </div>
      ) : null}

      {(tab === "activity" || tab === "clients") ? (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <select className="input" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} style={{ maxWidth: 240 }}>
            <option value="">Every operator</option>
            {realOperators.map((o) => <option key={o.organization_id} value={o.organization_id}>{o.name}</option>)}
          </select>
          {tab === "activity" ? (
            <select className="input" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} style={{ maxWidth: 200 }}>
              <option value="">Everything</option>
              <option value="request">In the app</option>
              <option value="event">Account events</option>
              <option value="signup">Signups</option>
            </select>
          ) : (
            <input className="input" placeholder="Search name, city, ZIP or operator" value={search}
                   onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />
          )}
        </div>
      ) : null}

      {/* A failed refresh must never look like fresh data. The list below may still be
          holding the last good response, so say so above it rather than only in the
          empty state — an empty state never shows while stale rows are on screen. */}
      {tabErr[tab] ? (
        <div className="alert alert-error" style={{ marginBottom: 12 }}>
          Couldn't refresh this from the server, so what's below may be out of date. Reload the page;
          if it keeps failing, sign out and back in.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
      {tab === "activity" ? (
        activity.length === 0 ? (
          <Empty>{tabErr.activity
            ? "Couldn't load the feed — this is not \u201cnothing happened\u201d. Reload the page; if it persists, sign in again."
            : orgFilter || kindFilter
            ? "Nothing matches those filters. Widen them to see the rest."
            : "Nothing yet across the ecosystem."}</Empty>
        ) : (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {activity.map((e, i) => {
              const [icon, label] = KIND[e.kind] || ["•", ""];
              return (
                <div key={`${e.at}:${e.kind}:${e.client_id || i}`} className="alist-row"
                     style={{ cursor: e.client_id ? "pointer" : "default" }}
                     onClick={() => openDetail(e.client_id)}>
                  <div style={{ width: 26, textAlign: "center", fontSize: 15 }} aria-hidden>{icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                      {[e.organization, e.client, label].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--text3)", flexShrink: 0 }}>{ago(e.at)}</div>
                </div>
              );
            })}
          </div>
        )
      ) : null}

      {tab === "operators" ? (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {realOperators.map((o) => (
            <div key={o.organization_id} className="alist-row" style={{ alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{o.name}</div>
                <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                  {[o.city && o.state ? `${o.city}, ${o.state}` : null,
                    `${o.active_clients} client${o.active_clients === 1 ? "" : "s"}`,
                    `${o.technicians} tech${o.technicians === 1 ? "" : "s"}`,
                    o.mrr > 0 ? `${money(o.mrr)}/mo${o.mrr_is_simulated ? " (test)" : ""}` : null].filter(Boolean).join(" · ")}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  <span className={`badge ${o.connected ? "badge-green" : "badge-gray"}`}>
                    {o.connected ? "Stripe connected" : "No Stripe"}
                  </span>
                  {o.billing_mode === "simulated" ? <span className="badge badge-gold">Test billing</span> : null}
                  <span className={`badge ${o.customer_app_enabled ? "badge-blue" : "badge-gray"}`}>
                    {o.customer_app_enabled ? `App on · ${o.app_logins} account${o.app_logins === 1 ? "" : "s"}` : "App off"}
                  </span>
                  <span className="badge badge-gray">
                    {o.saas_plan === "flat" ? "$99 flat" : `${(o.application_fee_bps / 100).toFixed(o.application_fee_bps % 100 ? 2 : 0)}% fee`}
                  </span>
                  {o.requests_open ? <span className="badge badge-red">{o.requests_open} waiting</span> : null}
                  {!o.onboarding_completed ? <span className="badge badge-gold">Onboarding</span> : null}
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text3)", flexShrink: 0, textAlign: "right" }}>
                {o.last_activity ? ago(o.last_activity) : "no activity"}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "clients" ? (
        clients.length === 0 ? (
          <Empty>{tabErr.clients
            ? "Couldn't load the client list \u2014 this is not an empty platform. Reload the page; if it persists, sign in again."
            : search ? "Nobody matches that."
            : orgFilter ? "That operator has no clients yet."
            : "No clients yet."}</Empty>
        ) : (
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              {clients.map((c) => (
                <div key={c.client_id} className="alist-row" style={{ cursor: "pointer" }}
                     onClick={() => openDetail(c.client_id)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
                      {[c.organization, c.city, c.frequency,
                        c.monthly ? `${money(c.monthly)}/mo${c.monthly_is_simulated ? " (test)" : ""}` : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {c.has_app_login ? <span className="badge badge-blue">App</span> : null}
                    {c.addons > 0 ? <span className="badge badge-purple">{c.addons} add-on{c.addons === 1 ? "" : "s"}</span> : null}
                    <span className={`badge ${c.active ? "badge-green" : "badge-gray"}`}>{c.active ? "Active" : c.status}</span>
                  </div>
                </div>
              ))}
          </div>
        )
      ) : null}
      </div>

      {/* The drill-in lives at page level, not inside the Clients tab: Activity rows open
          it too, and nesting it there made every Activity click a silent no-op on desktop. */}
      {!isMobile && detail ? (
        <div style={{ flex: "0 0 340px", position: "sticky", top: 76 }}>
          <ClientDetail d={detail} onClose={() => setDetail(null)} />
        </div>
      ) : null}
      </div>

      {tab === "templates" ? (
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>
            The starter menu operators copy into their own catalog. Editing one here changes what
            future adopters get — operators who already added it keep their own copy and their own price.
          </div>
          {tabErr.templates ? <Empty>Couldn't load the templates. Reload the page; if it persists, sign in again.</Empty> : null}
          {templates.map((t) => (
            <div key={t.id} className="card" style={{ padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{t.name}{t.builtin ? " (built-in)" : ""}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text2)" }}>{t.tagline}</div>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--text2)", textAlign: "right" }}>
                  {saved === t.id ? <span style={{ color: "var(--green, #1a7f37)", fontWeight: 600 }}>Saved</span>
                    : saved === `err:${t.id}` ? <span style={{ color: "var(--red, #c0392b)", fontWeight: 600 }}>Didn't save</span>
                    : null}
                  {saved === t.id || saved === `err:${t.id}` ? <span style={{ margin: "0 6px" }}>·</span> : null}
                  adopted by {t.adopted_by} · live in {t.live_in}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginTop: 10 }}>
                <label><span style={{ fontSize: 12, color: "var(--text2)" }}>Suggested price</span>
                  <PriceInput value={t.price} onSave={(v) => saveTemplate(t.id, { price: v })} />
                </label>
                <label><span style={{ fontSize: 12, color: "var(--text2)" }}>Default handling</span>
                  <Picker value={t.approval_mode} onSave={(v) => saveTemplate(t.id, { approval_mode: v })}>
                    <option value="auto">Apply right away</option>
                    <option value="review">Wait for the operator</option>
                  </Picker>
                </label>
                <label><span style={{ fontSize: 12, color: "var(--text2)" }}>Offered to operators</span>
                  <Picker value={t.active ? "1" : "0"} onSave={(v) => saveTemplate(t.id, { active: v === "1" })}>
                    <option value="1">Yes</option><option value="0">No</option>
                  </Picker>
                </label>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {isMobile && detail ? (
        <>
          <div className="modal-overlay" onClick={() => setDetail(null)} />
          <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 60, maxHeight: "85dvh", overflowY: "auto" }}>
            <ClientDetail d={detail} onClose={() => setDetail(null)} />
          </div>
        </>
      ) : null}
    </div>
  );
}

// A number field that cannot silently mean "clear this".
//
// `<input type="number">` reports value === "" for anything it cannot parse — "12,50",
// "45.5.", "1e" — which is indistinguishable from a deliberately emptied field. That made
// a typo write NULL and report "Saved". validity.badInput separates the two.
//
// It is controlled from a draft, so a rejected save or a server-normalised value (a price
// column is numeric(10,2): 45.678 comes back 45.68) is reflected back into the field
// instead of leaving the operator looking at a number the database does not hold.
function PriceInput({ value, onSave }) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  const [bad, setBad] = useState(false);
  useEffect(() => { setDraft(value == null ? "" : String(value)); setBad(false); }, [value]);
  return (
    <>
      <input className="input" type="number" step="0.01" min="0" value={draft}
             style={{ width: "100%", marginTop: 3, borderColor: bad ? "var(--red, #c0392b)" : undefined }}
             onChange={(e) => { setDraft(e.target.value); if (bad) setBad(false); }}
             onBlur={(e) => {
               if (e.target.validity.badInput) { setBad(true); return; }   // a typo is not "clear it"
               const raw = e.target.value.trim();
               const v = raw === "" ? null : Number(raw);
               if (v !== null && !Number.isFinite(v)) { setBad(true); return; }
               if (v === value) return;
               // "12,50" is a real way people type a price. Chrome does not flag it as
               // badInput — it silently drops the comma and leaves 1250, a 100x price
               // that then propagates to every operator who adopts this item next.
               // Anything an order of magnitude off gets one confirmation.
               if (v !== null && value != null && Number(value) > 0 && (v >= Number(value) * 10 || v * 10 <= Number(value))) {
                 if (!window.confirm(`Change the suggested price from $${Number(value).toFixed(2)} to $${v.toFixed(2)}?\n\nThat's what new operators will see when they add this item.`)) {
                   setDraft(String(value));
                   return;
                 }
               }
               onSave(v);
             }} />
      {bad ? <span style={{ fontSize: 11.5, color: "var(--red, #c0392b)" }}>Numbers only — nothing was saved.</span> : null}
    </>
  );
}

// A select that shows the choice immediately and rolls back only if the save fails,
// instead of visibly snapping to the old value for the length of a round-trip.
function Picker({ value, onSave, children }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <select className="input" value={draft} style={{ width: "100%", marginTop: 3 }}
            onChange={(e) => {
              const v = e.target.value;
              setDraft(v);
              Promise.resolve(onSave(v)).catch(() => setDraft(value));
            }}>{children}</select>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: "var(--accent)" } : undefined}>{value ?? "—"}</div>
      {sub ? <div className="stat-sub">{sub}</div> : null}
    </div>
  );
}

function Empty({ children }) {
  return <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--text2)" }}>{children}</div>;
}

function ClientDetail({ d, onClose }) {
  const c = d.client;
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-.01em" }}>{c.name}</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>{c.organization}</div>
        </div>
        <button className="close-btn" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <div style={{ marginTop: 12, display: "grid", gap: 5, fontSize: 14 }}>
        <Row k="Where" v={[c.city, c.state, c.zip].filter(Boolean).join(", ")} />
        <Row k="Plan" v={[c.frequency, c.dogs ? `${c.dogs} dog${c.dogs === 1 ? "" : "s"}` : null, c.deo_enabled ? "deodorizer" : null].filter(Boolean).join(" · ")} />
        <Row k="Status" v={c.active ? "Active" : c.status} />
        <Row k="Since" v={c.start_date} />
        <Row k="Ends" v={c.plan_ends_on} />
        <Row k="App login" v={c.has_app_login ? "Yes" : "No"} />
        <Row k="Visits (90d)" v={d.visits_90d} />
        <Row k="Last visit" v={d.last_visit} />
      </div>
      {d.addons?.length ? (
        <>
          <div style={{ fontSize: 12.5, color: "var(--text2)", marginTop: 14, fontWeight: 600 }}>ADD-ONS</div>
          {d.addons.map((a, i) => (
            <div key={i} style={{ fontSize: 14, marginTop: 4 }}>
              {a.name} · {a.status}{a.price ? ` · $${Number(a.price).toFixed(2)}` : ""}
            </div>
          ))}
        </>
      ) : null}
      {d.requests?.length ? (
        <>
          <div style={{ fontSize: 12.5, color: "var(--text2)", marginTop: 14, fontWeight: 600 }}>RECENT APP ACTIVITY</div>
          {d.requests.slice(0, 6).map((r, i) => (
            <div key={i} style={{ fontSize: 13.5, marginTop: 4, color: "var(--text2)" }}>
              {ago(r.at)} · {r.summary}
            </div>
          ))}
        </>
      ) : null}
      <div style={{ marginTop: 14, fontSize: 12, color: "var(--text3)" }}>
        Read-only. Changes belong to the operator who serves this client.
      </div>
    </div>
  );
}

function Row({ k, v }) {
  if (v == null || v === "") return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ color: "var(--text2)" }}>{k}</span>
      <span style={{ fontWeight: 500, textAlign: "right" }}>{v}</span>
    </div>
  );
}
